import { motion } from 'framer-motion';
import heatBlastImg from '../../assests/heat-blast.png';
import cicdImg from '../../assests/cicd.png';
import dockerImg from '../../assests/docker.png';
import postgresImg from '../../assests/postgresql.png';
import fastApiImg from '../../assests/fast-api.png';
import langGraphImg from '../../assests/langGraph.png';
import pytorchImg from '../../assests/pytorch.png';
import tensorflowImg from '../../assests/tensorflow.png';
import scilearnImg from '../../assests/sci-learn.png';
import skillsIcon from '../../assests/skills.png';

interface SkillItem {
  name: string;
  img?: string;
}

interface SkillCategory {
  title: string;
  color: string;
  skills: SkillItem[];
}

const categories: SkillCategory[] = [
  {
    title: 'AI & ML',
    color: '#00B92E',
    skills: [
      { name: 'LangGraph', img: langGraphImg },
      { name: 'LangChain' },
      { name: 'PyTorch', img: pytorchImg },
      { name: 'TensorFlow', img: tensorflowImg },
      { name: 'Transformers' },
      { name: 'RAG' },
      { name: 'LLM Orchestration' },
      { name: 'Multi-Agent Systems' },
      { name: 'Knowledge Graphs' },
      { name: 'Computer Vision' },
      { name: 'NLP' },
      { name: 'spaCy' },
      { name: 'Scikit-learn', img: scilearnImg },
      { name: 'Pandas' },
      { name: 'Weights & Biases' },
    ],
  },
  {
    title: 'Backend',
    color: '#3b82f6',
    skills: [
      { name: 'Python' },
      { name: 'FastAPI', img: fastApiImg },
      { name: 'Flask' },
      { name: 'Socket.IO' },
      { name: 'SQLAlchemy' },
      { name: 'Celery' },
    ],
  },
  {
    title: 'Frontend',
    color: '#8b5cf6',
    skills: [
      { name: 'React' },
      { name: 'Node.js' },
      { name: 'Tailwind CSS' },
      { name: 'Zustand' },
      { name: 'TanStack Query' },
      { name: 'TypeScript' },
    ],
  },
  {
    title: 'Databases',
    color: '#f59e0b',
    skills: [
      { name: 'PostgreSQL', img: postgresImg },
      { name: 'MongoDB' },
      { name: 'Redis' },
      { name: 'Neo4j' },
      { name: 'ChromaDB' },
      { name: 'Elasticsearch' },
    ],
  },
  {
    title: 'Cloud & DevOps',
    color: '#ef4444',
    skills: [
      { name: 'Microsoft Azure' },
      { name: 'Google Cloud (GCP)' },
      { name: 'AWS' },
      { name: 'Docker', img: dockerImg },
      { name: 'Nginx' },
      { name: 'CI/CD', img: cicdImg },
      { name: 'Git' },
    ],
  },
];

function SkillBadge({ skill, color }: { skill: SkillItem; color: string }) {
  return (
    <motion.span
      whileHover={{ scale: 1.05, y: -2 }}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium text-white/80 border border-white/10 bg-white/[0.04] hover:border-opacity-50 transition-all cursor-default"
      style={{
        borderColor: `${color}22`,
        backgroundColor: `${color}08`,
      }}
    >
      {skill.img && (
        <img
          src={skill.img}
          alt={skill.name}
          className="w-3.5 h-3.5 object-contain"
          style={{ filter: 'brightness(0) invert(1)' }}
        />
      )}
      {skill.name}
    </motion.span>
  );
}

export default function Skills() {
  return (
    <section id="skills" className="py-24 md:py-32 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="flex items-center gap-3 mb-12"
        >
          <img src={skillsIcon} alt="" className="w-8 h-8 object-contain" />
          <h2
            className="text-white font-bold section-label"
            style={{ fontSize: 'clamp(1.8rem, 4vw, 3rem)', letterSpacing: '0.05em' }}
          >
            Skills
          </h2>
        </motion.div>

        {/* Two-column layout: alien image + skills grid */}
        <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-12 lg:gap-16 items-start">
          {/* Left — Heat Blast alien */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="hidden lg:flex justify-center"
          >
            <div className="relative">
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background:
                    'radial-gradient(ellipse 80% 100% at 50% 70%, rgba(255,80,0,0.08) 0%, transparent 70%)',
                  filter: 'blur(20px)',
                  transform: 'scale(1.3)',
                }}
              />
              <motion.img
                src={heatBlastImg}
                alt="Heat Blast"
                className="relative w-[200px] xl:w-[240px] object-contain"
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 3.5, repeat: Infinity, ease: 'easeInOut' }}
              />
            </div>
          </motion.div>

          {/* Right — Skills categories grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
            {categories.map((cat, catIdx) => (
              <motion.div
                key={cat.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: catIdx * 0.1 }}
                className="space-y-3"
              >
                {/* Category label */}
                <div className="flex items-center gap-2 mb-3">
                  <div
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: cat.color }}
                  />
                  <h3
                    className="text-white text-xs font-bold tracking-widest uppercase"
                    style={{ color: cat.color }}
                  >
                    {cat.title}
                  </h3>
                </div>

                {/* Badges */}
                <div className="flex flex-wrap gap-2">
                  {cat.skills.map((skill) => (
                    <SkillBadge key={skill.name} skill={skill} color={cat.color} />
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
